import { Link } from 'react-router';
import {
  GuideLayout,
  GuideSection,
  GuideStep,
  GuideSteps,
  GuideTip,
} from '@/components/guides/guide-layout';
import { PhaseFiveGuideNotes } from '@/components/guides/phase-five-guide-notes';

const inlineLink =
  'font-medium text-brand-600 underline decoration-brand-300 underline-offset-2';

export function PotholeClaimRejectedNextStepsGuide() {
  return (
    <GuideLayout
      slug="pothole-claim-rejected-next-steps"
      category="Escalation"
      categoryColor="warning"
      title="Pothole Claim Rejected? Next Steps"
      seoTitle="Pothole Claim Rejected? Next Steps | Fixtyer"
      description="How to read a rejection, assess a Section 58 response, request the underlying records and decide when legal advice may help."
      readTime="8 min"
      updatedDate="2026-07-19"
      reviewedDate="2026-07-19"
      datePublished="2026-07-19"
      reviewedFor="England and Wales"
      contentOwner="Elevyn Technology Group Limited editorial team"
      sources={[
        {
          name: 'Highways Act 1980 section 58',
          url: 'https://www.legislation.gov.uk/ukpga/1980/66/section/58',
        },
        {
          name: 'GOV.UK find a legal adviser',
          url: 'https://www.gov.uk/find-legal-advice/find-legal-adviser',
        },
      ]}
      atAGlance={[
        {
          label: 'Answer first',
          value:
            'A rejection is a decision to assess, not proof that either side is right. Identify the stated reason and test it against the location-specific records.',
        },
        {
          label: 'Section 58',
          value:
            'A policy alone is not the same as completed evidence that the system operated at the location.',
        },
        {
          label: 'Legal advice',
          value:
            'Consider a regulated solicitor where injury, substantial loss, limitation or court action makes the risk material.',
        },
      ]}
      relatedGuides={[
        {
          slug: 'section-58-defence',
          title: 'Section 58 Defence',
          readTime: '7 min',
        },
        {
          slug: 'request-road-inspection-records',
          title: 'Request Road Inspection Records',
          readTime: '8 min',
        },
        {
          slug: 'small-claims-court-guide',
          title: 'Small Claims Court Guide',
          readTime: '10 min',
        },
      ]}
    >
      <GuideSteps>
        <GuideStep number={1} title="Separate the reasons">
          Mark each distinct point: wrong authority, disputed location, no proof of damage or
          causation, no breach alleged, Section 58, contributory negligence, missing documents or a
          procedural requirement. Do not answer a different reason from the one given.
        </GuideStep>
        <GuideStep number={2} title="Ask for the records behind factual assertions">
          Request the applicable inspection policy, completed inspections, defect reports, risk
          assessments and repairs for the defined location and period. Use the{' '}
          <Link to="/guides/request-road-inspection-records" className={inlineLink}>
            focused EIR template
          </Link>{' '}
          if the response does not provide them.
        </GuideStep>
        <GuideStep number={3} title="Reply with a short evidence table">
          For each disputed point, identify the authority&apos;s statement, the document relied on,
          your evidence and the precise clarification requested. Avoid claiming that one missing
          record automatically defeats the defence.
        </GuideStep>
      </GuideSteps>

      <GuideSection title="How to read a Section 58 response" icon={<span aria-hidden="true">58</span>}>
        <p className="mb-4">
          Section 58 asks whether the authority took the care reasonably required in all the
          circumstances. Compare the road classification and inspection frequency in the policy
          with completed inspections, recorded defects, response priorities and repairs for the
          relevant place and time.
        </p>
        <p>
          Also check whether the response addresses the statutory factors rather than merely
          attaching a generic policy. A discrepancy may need explanation, but it is not a promise
          of a different outcome. The deeper{' '}
          <Link to="/guides/section-58-defence" className={inlineLink}>
            Section 58 guide
          </Link>{' '}
          explains the factors and evidential limits.
        </p>
      </GuideSection>

      <GuideSection title="When to speak to a solicitor" icon={<span aria-hidden="true">§</span>}>
        <ul className="list-disc space-y-2 pl-5">
          <li>the incident caused personal injury or a substantial or complex loss;</li>
          <li>a limitation deadline may be approaching;</li>
          <li>the identity of the defendant or legal basis is disputed;</li>
          <li>you are considering issuing proceedings or have received formal court documents;</li>
          <li>costs, expert evidence or settlement terms are difficult to assess.</li>
        </ul>
        <GuideTip variant="info" title="Find regulated help">
          GOV.UK links to the Law Society&apos;s solicitor search for England and Wales and explains
          possible legal-aid or lower-cost routes. Check the adviser&apos;s regulatory status and
          terms before instructing them.
        </GuideTip>
      </GuideSection>

      <PhaseFiveGuideNotes />
    </GuideLayout>
  );
}
